import React, { useState, useEffect } from 'react';
import { searchAddresses } from '../../services/hereMapsService';
import { debounce } from 'lodash';
import { ClearableInput } from '@rio-cloud/rio-uikit';

interface AddressSearchProps {
  value?: string;
  disabled?: boolean;
  onAddressSelect?: (address: any) => void;
  getAddress?: (address: Address) => void;
  placeholder?: string;
  onClear?: () => {};
}

interface Address {
  street: string;
  houseNumber: string;
  district: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
  countryCode: string;
  label?: string;
  geoLocation?: { lat: number, lng: number };
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  value,
  onAddressSelect,
  placeholder = "Digite um endereço...",
  disabled,
  getAddress,
  onClear,
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSelected, setIsSelected] = useState(true);

  const debouncedSearch = debounce(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }

    setError(null);

    try {
      const results = await searchAddresses(searchQuery);
      setSuggestions(results);
    } catch (err) {
      setError('Erro ao buscar endereços. Tente novamente.');
      setSuggestions([]);
    }
  }, 500);

  useEffect(() => {
    if (isSelected) return;
    debouncedSearch(query);
    return () => {
      debouncedSearch.cancel();
    };
  }, [query]);

  useEffect(() => {
    if (value || value === '') {
      setQuery(value || '');
    }
  }, [value]);

  const handleAddressSelect = (address: any) => {
    setIsSelected(true);
    setQuery(address.address.label);
    setSuggestions([]);
    onAddressSelect?.(address.address.label || address.address.postalCode);
    getAddress?.({ ...address.address, geoLocation: address.position });
  };

  return <div className="relative w-full">
    <ClearableInput
      type="text"
      value={query}
      onChange={(e) => {
        setIsSelected(false);
        setQuery(e);
      }}
      placeholder={placeholder}
      disabled={disabled}
      onClear={() => {
        onAddressSelect?.(null);
        setIsSelected(false);
        setQuery('');
        onClear?.();
      }}
    />

    {error && (
      <div className="text-red-500 text-sm mt-1">{error}</div>
    )}

    {suggestions.length > 0 && (
      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.id}
            onClick={() => handleAddressSelect(suggestion)}
            className="p-2 hover:bg-gray-100 cursor-pointer"
          >
            <div className="font-medium">{suggestion.title}</div>
            <div className="text-sm text-gray-600">{suggestion.address.label}</div>
          </div>
        ))}
      </div>
    )}
  </div>
};

export default AddressSearch; 